// PERMANENT: About page — required for AdSense compliance. Do not remove.
import { Link } from "react-router-dom";
import { ArrowLeft, BookOpen, Compass, Sparkles, ArrowRight } from "lucide-react";
import SeoHead from "@/components/SeoHead";

const NAVY = "#1B2F5E";
const GOLD = "#C9A96E";

function Card({ icon: Icon, title, children }) {
  return (
    <div
      className="rounded-2xl px-6 sm:px-7 py-6 sm:py-7"
      style={{
        background: "#fff",
        border: `1px solid ${GOLD}44`,
        boxShadow: "0 8px 28px -18px rgba(27,47,94,0.22)",
      }}
    >
      <span
        className="inline-flex items-center justify-center w-10 h-10 rounded-full mb-4"
        style={{ background: `${GOLD}22`, color: GOLD }}
      >
        <Icon className="w-5 h-5" />
      </span>
      <h3 className="font-serif text-lg sm:text-xl font-semibold mb-2" style={{ color: NAVY }}>{title}</h3>
      <p className="text-sm sm:text-base leading-relaxed" style={{ color: "#4A5568" }}>{children}</p>
    </div>
  );
}

export default function AboutPage() {
  return (
    <div className="min-h-screen topo-bg" data-testid="about-page">
      <SeoHead
        title="About"
        description="About Summary Maps LLC — visual mind maps that help you read smarter and retain more of what you read."
      />
      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16 pb-20 sm:pb-24">
        <div className="max-w-3xl mx-auto" style={{ maxWidth: 720 }}>
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-brand mb-8" data-testid="back-link">
            <ArrowLeft className="w-4 h-4" /> Back to the journal
          </Link>

          <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: GOLD }}>About</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight" style={{ color: NAVY }}>
            About Summary Maps
          </h1>
          <p className="mt-6 text-lg sm:text-xl leading-relaxed" style={{ color: "#2D3748" }}>
            We help people read smarter and retain more — through visual mind maps that turn dense books
            into clear, memorable structure.
          </p>
        </div>

        <div className="mt-14 sm:mt-16 grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
          <Card icon={BookOpen} title="Who we are">
            Summary Maps LLC is a small team obsessed with the difference between reading a book and
            actually retaining what it taught us.
          </Card>
          <Card icon={Compass} title="Our mission">
            Make complex books accessible through clear, beautiful mind map summaries you can revisit,
            share, and remember.
          </Card>
          <Card icon={Sparkles} title="The journal">
            SummaryMaps Journal publishes essays and guides on mind mapping, book summaries, and visual
            learning for readers who want their reading to compound.
          </Card>
        </div>

        <div className="mt-14 sm:mt-16 max-w-3xl mx-auto" style={{ maxWidth: 720 }}>
          <h2 className="font-serif text-2xl sm:text-3xl font-semibold mb-4" style={{ color: NAVY }}>Why we built this</h2>
          <p className="text-base sm:text-lg leading-relaxed mb-4" style={{ color: "#2D3748" }}>
            Great ideas fade fast. Highlights pile up in notebooks that nobody revisits. We built Summary
            Maps because reading deserved a better container — one that holds structure, not just text.
          </p>
          <p className="text-base sm:text-lg leading-relaxed" style={{ color: "#2D3748" }}>
            The journal is where we work through the ideas out loud. The product is where those ideas
            become tools you can use.
          </p>

          <a
            href="https://summarymaps.com"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm sm:text-base font-medium transition-transform hover:-translate-y-0.5"
            style={{ background: NAVY, color: "#fff", border: `1px solid ${NAVY}` }}
            data-testid="about-app-link"
            onMouseEnter={(e) => { e.currentTarget.style.background = "#14224a"; e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = NAVY; e.currentTarget.style.borderColor = NAVY; e.currentTarget.style.color = "#fff"; }}
          >
            Try SummaryMaps free <ArrowRight className="w-4 h-4" />
          </a>

          <section
            className="mt-16 sm:mt-20 pt-10 sm:pt-12"
            style={{ borderTop: `1px solid ${GOLD}55` }}
            data-testid="editorial-standards"
          >
            <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: GOLD }}>
              Editorial
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold mb-4" style={{ color: NAVY }}>
              Our Editorial Standards
            </h2>
            <p className="text-base sm:text-lg leading-relaxed" style={{ color: "#2D3748" }}>
              The Summary Maps Journal is edited and published by Travis Moore, founder of Summary Maps.
              Some articles are drafted with AI assistance; we check claims before and after publishing and
              correct mistakes openly with a dated correction note. We are committed to providing accurate,
              original, and valuable content that helps our readers learn smarter.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
